/**
 * The mathematical progression model.
 *
 * ## Why this is not the GDD's exponential curve
 *
 * The GDD anchors its economy in the OGame formula family (references §1/§2),
 * where cost grows as `base·factor^level`. That family works beautifully up to
 * about level 40 and then stops dead: at factor 1.5 a level-1000 mine costs
 * ~1e177 titan. `scripts/simulate-progression.mjs --curve=exp` demonstrates it —
 * a maximally efficient player reaches level 36 after twelve years and can go
 * no further.
 *
 * The design calls for mines in the high hundreds, touching 1000 after a few
 * years of play, so the curves here are POLYNOMIAL instead:
 *
 *     cost(l)       = base · l^1.7
 *     production(l) = base · l^1.6
 *     energy(l)     = base · l^1.68     ← draw grows fastest
 *
 * Production and cost are close together, so a level always stays affordable;
 * the slowdown that keeps late levels meaningful comes from ENERGY, whose draw
 * outgrows both. That is also the GDD's own stated intent for the Titan-Schmelze
 * ("Skaliert exponentiell im Energiebedarf") — the throttle is the mechanic,
 * not an arbitrary cost wall.
 *
 * Exponents were not guessed: `scripts/tune-curve.mjs` sweeps them against the
 * design's milestone targets and prints the best fits. The chosen values hit
 * every target exactly (level 3 in ~3 min, 5 in ~17 min, 100 in ~3 days,
 * 1000 in ~5 years).
 *
 * Research keeps the classic exponential curve on purpose: its levels are meant
 * to stay in the tens, and an exponential wall is the right shape for that.
 */

import type { ResourceAmounts, ShipKey } from './types.js';
import { UNITS } from './data/units.js';

/** Universe speed factor ("Server-Geschwindigkeitsfaktor", GDD §2). */
export const UNIVERSE_SPEED = 1;

/* ------------------------------------------------------------------ *
 * Curve exponents — see the module comment for how they were chosen.  *
 * ------------------------------------------------------------------ */

/** Building cost exponent: cost(l) = base · l^COST_EXP. */
export const COST_EXP = 1.7;
/** Mine output exponent. */
export const PROD_EXP = 1.6;
/** Energy exponent. Above PROD_EXP so power is the real late-game constraint. */
export const ENERGY_EXP = 1.68;
/** Construction throughput in mass per hour at nanite level 0. */
export const BUILD_RATE = 150_000;

/** Slowest hull sets the speed of a fleet; a probe remains dramatically faster. */
export function fleetSpeed(fleet: Partial<Record<ShipKey, number>>): number {
  const speeds = (Object.entries(fleet) as [ShipKey, number][])
    .filter(([, count]) => count > 0)
    .map(([key]) => UNITS[key].speed);
  return speeds.length > 0 ? Math.min(...speeds) : 0;
}

/**
 * Travel time keeps today's balance at reference speed 300, then varies by
 * the slowest selected hull and applies event/talent flight-speed bonuses.
 * This makes mixed fleets meaningfully slower without changing the baseline.
 */
export function fleetTravelTime(baseMs: number, fleet: Partial<Record<ShipKey, number>>, flightSpeed = 1): number {
  const speed = fleetSpeed(fleet);
  if (speed <= 0) return 0;
  return Math.round(baseMs * (300 / speed) / Math.max(0.01, flightSpeed));
}

/** Cost of a building at `level` (1-indexed). */
export function levelCost(base: ResourceAmounts, level: number): ResourceAmounts {
  const scale = Math.pow(Math.max(1, level), COST_EXP);
  const out: ResourceAmounts = {};
  for (const [key, value] of Object.entries(base) as [keyof ResourceAmounts, number][]) {
    out[key] = Math.round(value * scale);
  }
  return out;
}

/**
 * Owner decision (2026-08-01): trade relay level 1 costs no credits so a
 * player who spent the starting credits in the casino still has a route back
 * to the credit economy. From level 2 onward the normal building curve applies.
 */
export function tradeRelayCost(
  level: number,
  base: ResourceAmounts = { titan: 600, silicate: 1_200, credit: 200 },
): ResourceAmounts {
  const cost = levelCost(base, level);
  if (level !== 1) return cost;
  const { credit: _credit, ...withoutCredits } = cost;
  return withoutCredits;
}

/**
 * Cost of a RESEARCH level — still exponential (GDD §2 "Steigerungsfaktor").
 * Research is meant to plateau in the tens, where an exponential wall is right.
 */
export function researchLevelCost(
  base: ResourceAmounts,
  factor: number,
  level: number,
): ResourceAmounts {
  const out: ResourceAmounts = {};
  for (const [key, value] of Object.entries(base) as [keyof ResourceAmounts, number][]) {
    out[key] = Math.round(value * Math.pow(factor, level - 1));
  }
  return out;
}

/** Cumulative building cost for levels 1..level (closed form of the integral). */
export function cumulativeCost(base: number, level: number): number {
  let total = 0;
  for (let l = 1; l <= level; l++) total += base * Math.pow(l, COST_EXP);
  return Math.round(total);
}

/**
 * Energy-independent base income per hour.
 *
 * Bootstraps a brand-new colony so the first upgrades land in seconds rather
 * than minutes. It is a constant, so it is ~all of the income at level 0 and
 * numerically irrelevant past level ~20 — it accelerates the opening without
 * touching the shape of the curve.
 */
export const BASE_INCOME_TITAN = 2_400;
export const BASE_INCOME_SILICATE = 1_200;

/** Mine output per hour at `level`: base · l^PROD_EXP. */
export function mineProduction(basePerHour: number, level: number): number {
  if (level <= 0) return 0;
  return basePerHour * Math.pow(level, PROD_EXP) * UNIVERSE_SPEED;
}

/**
 * Temperature factor for Isotop-3 synthesis (GDD §2: "Kältere Welten bieten
 * einen thermodynamischen Vorteil"). OGame's deuterium curve.
 */
export function isotopeTempFactor(temperatureC: number): number {
  return Math.min(2, Math.max(0.4, 1.44 - 0.004 * temperatureC));
}

/** Solar plant output in MW. Same exponent as mines — power keeps pace. */
export function energyProduction(level: number): number {
  if (level <= 0) return 0;
  return Math.round(660 * Math.pow(level, PROD_EXP));
}

/**
 * Energy draw of one mine at `level`. The exponent is deliberately ABOVE the
 * production exponent: every mine level makes the power balance a little
 * worse, which is what turns "just upgrade the mine" into a real decision and
 * what slows the late game without an artificial cost wall.
 */
export function mineEnergyUse(level: number): number {
  if (level <= 0) return 0;
  return Math.round(228 * Math.pow(level, ENERGY_EXP));
}

/** Mines run at prod/consumption when energy is short (GDD §1). */
export function energyFactor(produced: number, consumed: number): number {
  if (consumed <= 0) return 1;
  return Math.min(1, produced / consumed);
}

/* ------------------------------------------------------------------ *
 * Times. `speedModifier` comes from modifiers.ts (event buffs).       *
 * ------------------------------------------------------------------ */

/**
 * Build time in hours. The Nano-Fabrikationszentrum halves it per level
 * (GDD §1); `speedModifier` carries event buffs like "Bautempo +30 %".
 */
export function buildTimeHours(
  cost: ResourceAmounts,
  naniteLevel: number,
  speedModifier = 1,
): number {
  const mass = (cost.titan ?? 0) + (cost.silicate ?? 0);
  return mass / (BUILD_RATE * Math.pow(2, naniteLevel) * UNIVERSE_SPEED * speedModifier);
}

/** Research time in hours; the lab divisor implements GDD §1. */
export function researchTimeHours(
  cost: ResourceAmounts,
  labLevel: number,
  speedModifier = 1,
): number {
  const mass = (cost.titan ?? 0) + (cost.silicate ?? 0) + (cost.isotope ?? 0);
  return mass / (2_000 * (1 + labLevel) * UNIVERSE_SPEED * speedModifier);
}

/** Per-unit shipyard time. */
export function unitTimeHours(
  cost: ResourceAmounts,
  naniteLevel: number,
  speedModifier = 1,
): number {
  const mass = (cost.titan ?? 0) + (cost.silicate ?? 0);
  return mass / (60_000 * Math.pow(2, naniteLevel) * UNIVERSE_SPEED * speedModifier);
}

/* ------------------------------------------------------------------ *
 * Storage                                                             *
 * ------------------------------------------------------------------ */

/** Capacity without any storage building. */
export const BASE_STORAGE: Record<'titan' | 'silicate' | 'isotope', number> = {
  titan: 20_000,
  silicate: 20_000,
  isotope: 10_000,
};

/**
 * Storage capacity: base · (1 + level)^1.9.
 *
 * The exponent sits just above the production exponent so a depot level buys
 * slightly more than a mine level produces — storage stays a chore you must
 * keep up with, never a wall you cannot pass.
 */
export const STORAGE_EXP = 1.9;

export function storageCapacity(base: number, level: number): number {
  return Math.round(base * Math.pow(1 + level, STORAGE_EXP));
}

/* ------------------------------------------------------------------ *
 * Progression                                                         *
 * ------------------------------------------------------------------ */

/**
 * XP to advance FROM `level` to level+1 — polynomial (GDD §2).
 *
 * Tuned against the new economy so account level ~50 (the GDD's Ring-1
 * endgame gate) lands around the time mines reach ~1000.
 */
export function xpForNextLevel(level: number): number {
  return 300 * level * level;
}

/** 1 XP per 500 resources invested. */
export function xpForSpentResources(cost: ResourceAmounts): number {
  const mass =
    (cost.titan ?? 0) + (cost.silicate ?? 0) + (cost.isotope ?? 0) + (cost.aether ?? 0);
  return Math.floor(mass / 500);
}

/**
 * Moon chance from orbital debris (GDD §3): 1 % per 100 000 debris, cap 20 %.
 */
export function moonChance(debrisTitan: number, debrisSilicate: number): number {
  const total = debrisTitan + debrisSilicate;
  if (total < 100_000) return 0;
  return Math.min(0.2, Math.floor(total / 100_000) * 0.01);
}

/** Plunder cap — at most 50 % of stored resources (GDD §5 step 4). */
export const PLUNDER_CAP = 0.5;

/** Warp booster cap — flight time reduction of at most 30 % (GDD §2). */
export const WARP_BOOST_MAX = 0.3;

/** Chronos crystals charged per started half hour of removed time. */
export const CHRONOS_PER_HALF_HOUR = 1;

/**
 * Chronos price to FINISH a job outright.
 *
 * Deliberately not the GDD's "maximal 50 % der verbleibenden Bauzeit" partial
 * boost: an owner decision, on the grounds that a premium price a player is
 * asked to approve must buy the whole outcome. A price that removes half the
 * wait and leaves the rest is the shape players misread most often — they pay,
 * the bar is still there, and the currency feels stolen. One crystal per
 * started half hour of the FULL remaining time, and the job is done.
 */
export function completionCost(remainingMs: number): number {
  if (remainingMs <= 0) return 0;
  return Math.max(1, Math.ceil(remainingMs / (30 * 60_000)) * CHRONOS_PER_HALF_HOUR);
}

/** Merchant tax: base 15 %, −1 %/logistics level, floor 3 % (GDD research table). */
export function merchantTax(logisticsLevel: number): number {
  return Math.max(0.03, 0.15 - 0.01 * logisticsLevel);
}

/**
 * Simultaneous fleet-mission capacity granted by the Schiffshangar (owner
 * decision, no GDD section — see docs/ROADMAP.md Phase 4). Deliberately
 * front-loaded: building it at all already covers today's two mission
 * types (expedition + harvest, `state.expedition`/`state.harvest`), so the
 * first level is the biggest single jump — sqrt-shaped growth after that
 * means every further slot costs progressively more levels. That is the
 * whole point: it caps how many fleets a player can have away at once
 * without capping raw ship count, so a real-money or time lead can't turn
 * into an unlimited number of simultaneous fleets — design fill-in, tuned
 * for readability of the curve rather than a specific target date.
 */
export function fleetHangarCapacity(level: number): number {
  if (level <= 0) return 0;
  return 1 + Math.floor(Math.sqrt(level));
}

/**
 * Symmetric abstract distance for future real-coordinate fleet travel.
 * Crossing a ring dominates changing systems, which dominates moving within
 * one system; the weights keep those strategic layers visibly separate.
 */
export function travelDistance(
  a: { ring: number; system: number; position: number },
  b: { ring: number; system: number; position: number },
): number {
  return Math.abs(a.ring - b.ring) * 10_000
    + Math.abs(a.system - b.system) * 100
    + Math.abs(a.position - b.position);
}
